/* ═══════════════════════════════════════════════════════════════════════════
   a11y.js  —  automatic accessibility engine for every Frappe website page

   Architecture mirrors animations.js: loads via web_include_js, auto-applies
   to any Frappe page without developer intervention.

   SCHEMA (optional — define in <head> via Website HQ Settings):
     window.A11ySchema = {
       profile:  'standard' | 'high-contrast' | 'minimal',
       settings: {
         skipLink:     true,
         skipLinkText: 'Skip to main content',
         landmarks:    true,
         lang:         true,
         iconButtons:  true,
         pageHeading:  true,
         disclosures:  true,
         decorativeLogos: true,   // login-card logos with no alt: decorative
         scrollRegions:   true,   // boxes that scroll sideways: keyboard access and a name
       }
     }

   OPT-OUT:
     data-no-a11y on <body>          skip entire engine for this page
     data-no-a11y on any element     skip that element and its descendants
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var schema  = window.A11ySchema || {};
  var profile = schema.profile || 'standard';

  var settings = Object.assign({
    skipLink:     true,
    skipLinkText: 'Skip to main content',
    landmarks:    true,
    lang:         true,
    iconButtons:  true,
    pageHeading:  true,
    disclosures:  true,
    decorativeLogos: true,
    scrollRegions:   true,
  }, schema.settings || {});

  /* Minimal profile: only focus ring (CSS-only), no JS enhancements */
  if (profile === 'minimal') {
    settings.skipLink    = false;
    settings.iconButtons = false;
    settings.disclosures = false;
    settings.decorativeLogos = false;
    settings.scrollRegions   = false;
  }

  var SKIP_PATHS = [
    '/register', '/update-password', '/complete-signup',
    '/forgot', '/verify', '/qrcode', '/printview', '/printpreview',
  ];
  // '/login' intentionally not skipped: raftor_websitehq/www/login.html wraps
  // its content in <main class="page_content"> and labels controls directly,
  // so the generic skip-link/landmark/icon-button pass here is safe to apply.


  /* ── Skip link ──────────────────────────────────────────────────────────── */
  /* Visually hidden until the first Tab press; positions itself at the top of
     the viewport and focuses the main content area, letting keyboard users
     jump past repeated navigation on every page.                             */

  function injectSkipLink() {
    if (!settings.skipLink) return;
    if (document.querySelector('.a11y-skip-link')) return;

    /* Find the main content anchor — try in order of specificity */
    var target = document.querySelector('main')
      || document.querySelector('.page_content')
      || document.querySelector('[id="page-content"]')
      || document.querySelector('.page-content-wrapper');

    if (target && !target.id) target.id = 'a11y-main';

    var link = document.createElement('a');
    link.className   = 'a11y-skip-link';
    link.href        = '#' + (target ? target.id : 'a11y-main');
    link.textContent = settings.skipLinkText;
    document.body.insertBefore(link, document.body.firstChild);
  }


  /* ── Landmark roles ─────────────────────────────────────────────────────── */
  /* Frappe website pages use divs, not semantic HTML landmarks.  Adding ARIA
     landmark roles lets screen-reader users jump between page regions with
     a single key (e.g. F6 / NVDA region shortcut).                          */

  function applyLandmarks() {
    if (!settings.landmarks) return;

    /* main */
    var main = document.querySelector('main')
      || document.querySelector('.page_content')
      || document.querySelector('[id^="page-"]');
    if (main) {
      if (!main.id) main.id = 'a11y-main';
      if (main.tagName !== 'MAIN' && !main.getAttribute('role')) {
        main.setAttribute('role', 'main');
      }
    }

    /* nav */
    document.querySelectorAll('.navbar, nav').forEach(function (nav) {
      if (!nav.getAttribute('aria-label') && !nav.getAttribute('aria-labelledby')) {
        nav.setAttribute('aria-label', 'Main navigation');
      }
      if (nav.tagName !== 'NAV') nav.setAttribute('role', 'navigation');
    });

    /* footer */
    var footer = document.querySelector('footer, .web-footer, .footer');
    if (footer && footer.tagName !== 'FOOTER' && !footer.getAttribute('role')) {
      footer.setAttribute('role', 'contentinfo');
    }
  }


  /* ── HTML lang ──────────────────────────────────────────────────────────── */
  /* Screen readers use the lang attribute to select the correct pronunciation
     rules.  Frappe rarely sets it; we derive the value from Frappe boot data
     when available so it matches the active language.                        */

  function ensureLang() {
    if (!settings.lang) return;
    var html = document.documentElement;
    if (html.getAttribute('lang')) return;
    var lang = (window.frappe && window.frappe.boot && window.frappe.boot.lang) || 'en';
    html.setAttribute('lang', lang);
  }


  /* ── One top-level heading ──────────────────────────────────────────────── */
  /* Pages built by Frappe or the shop (sign-in, catalogue, cart, account) often
     have no <h1>.  Every page should have exactly one, so the first visible page
     title is announced as level 1 (no visual change); with none to use, the
     document title is added for screen readers only.  A heading that arrives
     later (a page drawn in the browser) replaces the one added here.          */

  var ADDED = 'data-a11y-h1';

  function ensurePageHeading() {
    if (!settings.pageHeading) return;
    var real = document.querySelector('h1:not([' + ADDED + ']), [role="heading"][aria-level="1"]');
    var ours = document.querySelector('[' + ADDED + ']');
    if (real) { if (ours && ours !== real) ours.remove(); return; }
    if (ours) return;
    var scope = document.querySelector('main, .page_content, .page-content-wrapper') || document.body;
    var title = null;
    scope.querySelectorAll('.page-title, .product-title, .page-header h2, .page-header h3, h2, h3').forEach(function (el) {
      if (!title && /\p{L}{3,}/u.test(el.textContent)) title = el;   /* a real title, not a count or a price */
    });
    if (title) {
      title.setAttribute('role', 'heading'); title.setAttribute('aria-level', '1'); title.setAttribute(ADDED, '');
      return;
    }
    var text = (document.title || '').replace(/^.*? - /, '').trim();
    if (!text) return;
    var h = document.createElement('h1');
    h.className = 'sr-only'; h.setAttribute(ADDED, ''); h.textContent = text;
    scope.insertBefore(h, scope.firstChild);
  }


  /* ── Icon-only buttons ──────────────────────────────────────────────────── */
  /* Buttons or links that contain only an icon (SVG / <i>) with no visible
     text are invisible to screen readers unless they carry an aria-label.
     We derive the label from the element's title or data-label attribute.   */

  function fixIconButtons(root) {
    if (!settings.iconButtons) return;
    var sel = [
      'button:not([aria-label]):not([aria-labelledby])',
      'a[role="button"]:not([aria-label])',
      'a[href]:not([aria-label]):not([aria-labelledby])',
    ].join(', ');

    try {
      qsa(root, sel).forEach(function (el) {
        if (el.closest('[data-no-a11y]')) return;
        if (el.textContent.trim()) return;    /* has visible text — fine */
        if (el.querySelector('img[alt]:not([alt=""])')) return;   /* a picture with a description names it */
        var label = el.getAttribute('title')
          || el.getAttribute('data-original-title')
          || el.getAttribute('data-label')
          || el.getAttribute('data-toggle');
        /* A link with only an icon is named after where it goes: /wishlist -> "Wishlist". */
        if (!label && el.tagName === 'A' && el.querySelector('svg, i, .icon')) {
          var last = (el.getAttribute('href') || '').split(/[?#]/)[0].split('/').filter(Boolean).pop() || '';
          try { last = decodeURIComponent(last); } catch (_) {}
          label = last.replace(/[-_]+/g, ' ').trim();
          if (label) label = label.charAt(0).toUpperCase() + label.slice(1);
        }
        /* A button with only an icon is named after its own id: id="image-view" -> "Image view". */
        if (!label && el.tagName === 'BUTTON' && el.id) {
          label = el.id.replace(/[-_]+/g, ' ').trim();
          if (label) label = label.charAt(0).toUpperCase() + label.slice(1);
        }
        if (label) el.setAttribute('aria-label', label);
      });
    } catch (_) {}
  }


  /* ── Decorative images inside links ────────────────────────────────────── */
  /* An <img> inside an <a> or <button> that lacks alt text confuses screen
     readers: they fall back to reading the filename aloud.  When the parent
     element provides the accessible label, the image should be decorative
     (alt="") so readers skip it.                                             */

  function fixLinkedImages(root) {
    try {
      qsa(root, 'a img, button img').forEach(function (img) {
        if (img.closest('[data-no-a11y]')) return;
        /* Only touch images that have no alt attribute at all */
        if (!img.hasAttribute('alt')) img.setAttribute('alt', '');
      });
    } catch (_) {}
  }


  /* ── Decorative logos ─────────────────────────────────────────
     Frappe's login card draws the site logo with no alt attribute, so a screen reader reads out its file
     name. The heading beside it already says where you are, so the logo is decorative: an empty alt.       */

  function fixDecorativeLogos(root) {
    if (!settings.decorativeLogos) return;
    try {
      qsa(root, 'img.app-logo:not([alt])').forEach(function (img) {
        if (!img.closest('[data-no-a11y]')) img.setAttribute('alt', '');
      });
    } catch (_) {}
  }


  /* ── Boxes that scroll sideways ───────────────────────────────
     A row of cards or a wide table that scrolls, with nothing in it the keyboard can reach from where a
     person is, is a dead end without a pointer. It is given tabindex 0 (so the arrow keys move it) and,
     when a heading names it, a region role with that heading as its name. Checked when the page has
     settled and whenever the window or the box changes size, because a row is only scrollable once its
     pictures and fonts are in.                                                                              */

  var SCROLLERS = new WeakSet();
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function regionName(box) {
    var label = box.getAttribute('aria-label');
    if (label) return label;
    var scope = box.closest('section, .section, [role="region"], article') || box.parentElement;
    var heading = scope && scope.querySelector('h1, h2, h3, [role="heading"], .section-title');
    return heading ? heading.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function checkScrollRegion(box) {
    var scrolls = box.scrollWidth > box.clientWidth + 4 && /auto|scroll/.test(window.getComputedStyle(box).overflowX);
    var mine = box.hasAttribute('data-a11y-scroll');
    if (scrolls && (mine || (!box.hasAttribute('tabindex') && !box.querySelector(FOCUSABLE)))) {   /* a box that already holds something to tab to needs nothing added */
      if (!mine) { box.setAttribute('tabindex', '0'); box.setAttribute('data-a11y-scroll', ''); }
      if (!box.hasAttribute('role')) {
        var name = regionName(box);
        if (name) { box.setAttribute('role', 'region'); box.setAttribute('aria-label', name); }
      }
    } else if (mine && !scrolls) {   /* it stopped scrolling (a wider window): what the engine added is taken back */
      ['tabindex', 'role', 'aria-label', 'data-a11y-scroll'].forEach(function (a) { box.removeAttribute(a); });
    }
  }

  function watchScroller(box) {
    checkScrollRegion(box);
    if (!SCROLLERS.has(box) && window.ResizeObserver) {
      SCROLLERS.add(box);
      new ResizeObserver(function (entries) { entries.forEach(function (e) { checkScrollRegion(e.target); }); }).observe(box);
    }
  }

  function fixScrollRegions() {
    if (!settings.scrollRegions) return;
    try {
      var boxes = document.querySelectorAll('*'), found = [];
      /* read first, write after: no layout is forced between the measurements */
      for (var i = 0; i < boxes.length && i < 6000; i++) {
        var box = boxes[i];
        if (box.scrollWidth > box.clientWidth + 4 || box.hasAttribute('data-a11y-scroll')) found.push(box);
      }
      found.forEach(function (box) { if (!box.closest('[data-no-a11y]')) watchScroller(box); });
    } catch (_) {}
  }

  /* One pass for a whole burst of changes: a page adds hundreds of nodes while it draws, and each used to ask for its own check. */
  var scrollTimer = null, scrollFirst = 0;
  function queueScrollCheck() {
    if (!settings.scrollRegions) return;
    var now = Date.now();
    if (!scrollFirst) scrollFirst = now;
    clearTimeout(scrollTimer);
    /* After the changes go quiet for half a second, or at the latest two seconds after the first one (a page that keeps changing must not keep this waiting for ever). */
    scrollTimer = setTimeout(function () {
      scrollFirst = 0;
      (window.requestIdleCallback || function (f) { setTimeout(f, 1); })(function () { fixScrollRegions(); }, { timeout: 1500 });
    }, Math.max(0, Math.min(500, 2000 - (now - scrollFirst))));
  }


  /* ── Buttons that open and close something ──────────────────────────────── */
  /* A button that points at another element with aria-controls but says nothing
     about whether that element is open (a folding group in a menu, from any
     plugin) is announced as a plain button.  It is given aria-expanded from
     whether the element it controls is shown, and that follows the element
     afterwards however it is opened or closed.  A button that already carries
     aria-expanded is left to whoever set it (Bootstrap's own toggler does).   */

  function isShown(target) {
    if (target.hidden) return false;
    var style = window.getComputedStyle(target);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function fixDisclosures(root) {
    if (!settings.disclosures) return;
    try {
      qsa(root, 'button[aria-controls]:not([aria-expanded]), [role="button"][aria-controls]:not([aria-expanded])').forEach(function (btn) {
        if (btn.closest('[data-no-a11y]')) return;
        var target = document.getElementById(btn.getAttribute('aria-controls'));
        if (!target) return;
        function sync() { btn.setAttribute('aria-expanded', isShown(target) ? 'true' : 'false'); }
        sync();
        new MutationObserver(sync).observe(target, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
        btn.addEventListener('click', function () { setTimeout(sync, 0); });
      });
    } catch (_) {}
  }


  /* ── Utility ────────────────────────────────────────────────────────────── */

  function qsa(root, sel) {
    try {
      var hits = root.querySelectorAll ? Array.from(root.querySelectorAll(sel)) : [];
      if (root.matches && root.matches(sel)) hits.unshift(root);
      return hits;
    } catch (_) { return []; }
  }


  /* ── Scan ───────────────────────────────────────────────────────────────── */

  function scan(root) {
    fixIconButtons(root);
    fixLinkedImages(root);
    fixDecorativeLogos(root);
    fixDisclosures(root);
    ensurePageHeading();
    queueScrollCheck();   /* measured after layout, once per burst of changes */
  }


  /* ── Boot ───────────────────────────────────────────────────────────────── */

  function boot() {
    var path = window.location.pathname.replace(/\/$/, '');
    if (SKIP_PATHS.indexOf(path) !== -1) return;
    if (document.body && document.body.hasAttribute('data-no-a11y')) return;

    ensureLang();
    injectSkipLink();
    applyLandmarks();
    scan(document);

    /* MutationObserver: apply fixes to dynamically injected content
       (Frappe AJAX page transitions, lazy-loaded components, etc.)         */
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          scan(node);
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

}());
